import {
  SELLING_PLAN_GROUP_ADDON_TAG,
  SELLING_PLAN_GROUP_TRENDING_LISTS_TAG,
  SHOPIFY_APP_ID,
} from "@smartrr/shared/constants";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import {
  ISmartrrSellingPlan,
  ISmartrrSellingPlanGroup,
  ISmartrrSellingPlanGroupWithEnablementMap,
  SellingPlanConfigs,
} from "@smartrr/shared/entities/SellingPlanGroup";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { isT } from "@smartrr/shared/utils/isT";
import {
  getPlanAndEligiblePurchasablesForSmartrrSellingPlanId,
  getSmartrrSellingPlanToProductAndVariantMapsFromSellingPlanGroups,
} from "@smartrr/shared/utils/sellingPlans";
import { isVariantInStock } from "@smartrr/shared/utils/variants";
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from "react";

import { usePurchasablesForSubscribing } from "@customer-app/account/views/OperationAquarius/AquariusInner/hooks/usePurchasablesForSubscribing";
import { CustomerSubReducer } from "@customer-app/state/typedCustomerRedux";

import { useCleanedAndSortedPurchables } from "./purchasables";
import { useSmartrrCustomerSelector } from "../typedCustomerReduxHooks";

export interface ISellingPlanGroupsReducerState {
  sellingPlansIsLoading: boolean;
  addOnsConfigIsLoading: boolean;
  trendingConfigIsLoading: boolean;

  sellingPlanGroupList: ISmartrrSellingPlanGroup[];
  addOnsConfigs: ISmartrrSellingPlanGroup[];
  trendingConfigs: ISmartrrSellingPlanGroup[];

  sellingPlanGroupsByVariantId: {
    [variantId: string]: ISmartrrSellingPlanGroup[];
  };
  sellingPlanGroupsByProductId: {
    [productId: string]: ISmartrrSellingPlanGroup[];
  };
  sellingPlanGroupsWithEnablementMap: ISmartrrSellingPlanGroupWithEnablementMap[];
}

const initialState: ISellingPlanGroupsReducerState = {
  sellingPlansIsLoading: true,
  sellingPlanGroupList: [],
  addOnsConfigIsLoading: true,
  trendingConfigIsLoading: true,
  addOnsConfigs: [],
  trendingConfigs: [],
  sellingPlanGroupsByProductId: {},
  sellingPlanGroupsByVariantId: {},
  sellingPlanGroupsWithEnablementMap: [],
};

export const sellingPlanGroupsReducer: CustomerSubReducer<ISellingPlanGroupsReducerState> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case "SELLING_PLAN_GROUPS_LOADING": {
      return {
        ...state,
        sellingPlansIsLoading: true,
      };
    }
    case "ADD_ONS_CONFIG_LOADING": {
      return {
        ...state,
        addOnsConfigIsLoading: true,
      };
    }
    case "TRENDING_LISTS_CONFIG_LOADING": {
      return {
        ...state,
        trendingConfigIsLoading: true,
      };
    }
    case "SELLING_PLAN_GROUPS_ERROR": {
      return {
        ...state,
        sellingPlansIsLoading: false,
      };
    }
    case "ADD_ONS_CONFIG_ERROR": {
      return {
        ...state,
        sellingPlansIsLoading: false,
      };
    }
    case "TRENDING_LISTS_CONFIG_ERROR": {
      return {
        ...state,
        trendingConfigIsLoading: false,
      };
    }
    case "SELLING_PLAN_GROUPS_ENABLEMENT_MAP": {
      return {
        ...state,
        sellingPlanGroupsWithEnablementMap: action.payload.sellingPlanGroupsWithEnablementMap,
      };
    }
    case "SELLING_PLAN_GROUPS_SUCCESS": {
      const { sellingPlanGroupsByProductId, sellingPlanGroupsByVariantId } =
        getSmartrrSellingPlanToProductAndVariantMapsFromSellingPlanGroups(action.payload.sellingPlanGroups);

      return {
        ...state,
        sellingPlansIsLoading: false,
        sellingPlanGroupList: cloneDeep(action.payload.sellingPlanGroups),
        sellingPlanGroupsByProductId: Object.keys(sellingPlanGroupsByProductId).reduce(
          (
            accum: {
              [productId: string]: ISmartrrSellingPlanGroup[];
            },
            productId
          ) => {
            const value = sellingPlanGroupsByProductId[Number(productId)];
            return {
              ...accum,
              [productId]: mergeAndReplaceOldSellingPlanGroups(accum[productId] || [], value),
            };
          },
          state.sellingPlanGroupsByProductId
        ),
        sellingPlanGroupsByVariantId: Object.keys(sellingPlanGroupsByVariantId).reduce(
          (
            accum: {
              [variantId: string]: ISmartrrSellingPlanGroup[];
            },
            variantId
          ) => {
            const value = sellingPlanGroupsByVariantId[Number(variantId)];
            return {
              ...accum,
              [variantId]: mergeAndReplaceOldSellingPlanGroups(accum[variantId] || [], value),
            };
          },
          state.sellingPlanGroupsByVariantId
        ),
      };
    }
    case "ADD_ONS_CONFIG_SUCCESS": {
      const { sellingPlanGroupsByProductId, sellingPlanGroupsByVariantId } =
        getSmartrrSellingPlanToProductAndVariantMapsFromSellingPlanGroups(action.payload.addOnsConfigs);

      return {
        ...state,
        addOnsConfigIsLoading: false,
        addOnsConfigs: action.payload.addOnsConfigs,
        sellingPlanGroupsByProductId: Object.keys(sellingPlanGroupsByProductId).reduce((accum, productId) => {
          const value = sellingPlanGroupsByProductId[Number(productId)];
          return {
            ...accum,
            [productId]: mergeAndReplaceOldSellingPlanGroups(accum[productId] || [], value),
          };
        }, state.sellingPlanGroupsByProductId),
        sellingPlanGroupsByVariantId: Object.keys(sellingPlanGroupsByVariantId).reduce((accum, variantId) => {
          const value = sellingPlanGroupsByVariantId[Number(variantId)];
          return {
            ...accum,
            [variantId]: mergeAndReplaceOldSellingPlanGroups(accum[variantId] || [], value),
          };
        }, state.sellingPlanGroupsByVariantId),
      };
    }
    case "TRENDING_LISTS_CONFIG_SUCCESS": {
      const { sellingPlanGroupsByProductId, sellingPlanGroupsByVariantId } =
        getSmartrrSellingPlanToProductAndVariantMapsFromSellingPlanGroups(action.payload.trendingConfigs);

      return {
        ...state,
        trendingConfigIsLoading: false,
        trendingConfigs: action.payload.trendingConfigs,
        sellingPlanGroupsByProductId: Object.keys(sellingPlanGroupsByProductId).reduce((accum, productId) => {
          const value = sellingPlanGroupsByProductId[Number(productId)];
          return {
            ...accum,
            [productId]: mergeAndReplaceOldSellingPlanGroups(accum[productId] || [], value),
          };
        }, state.sellingPlanGroupsByProductId),
        sellingPlanGroupsByVariantId: Object.keys(sellingPlanGroupsByVariantId).reduce((accum, variantId) => {
          const value = sellingPlanGroupsByVariantId[Number(variantId)];
          return {
            ...accum,
            [variantId]: mergeAndReplaceOldSellingPlanGroups(accum[variantId] || [], value),
          };
        }, state.sellingPlanGroupsByVariantId),
      };
    }
    default: {
      return state;
    }
  }
};

function mergeAndReplaceOldSellingPlanGroups(
  oldSellingPlanGroups: ISmartrrSellingPlanGroup[],
  newSellingPlanGroups: ISmartrrSellingPlanGroup[]
) {
  const newSellingPlanIds = new Set(
    flatten(newSellingPlanGroups.map(spg => spg.sellingPlans.map(sellingPlan => sellingPlan.shopifyNumericId)))
  );
  return oldSellingPlanGroups
    .filter(
      // get rid of liquid version (selling plan group ID doesn't match)
      spg => !spg.sellingPlans.some(sellingPlan => newSellingPlanIds.has(sellingPlan.shopifyNumericId))
    )
    .concat(newSellingPlanGroups);
}

export function usePlanAndEligiblePurchasablesForSellingPlanId(
  purchasables: IPurchasable[],
  sellingPlanId: string | undefined
):
  | {
      sellingPlan: ISmartrrSellingPlan;
      purchasables: IPurchasable[];
    }
  | undefined {
  const { sellingPlanGroupsByProductId } = useSmartrrCustomerSelector(state => state.sellingPlanGroups);
  if (!sellingPlanId) {
    return;
  }
  return getPlanAndEligiblePurchasablesForSmartrrSellingPlanId(
    purchasables,
    sellingPlanId,
    sellingPlanGroupsByProductId
  );
}

// for selling plan frequency selector
export function useSellingPlanGroupForSellingPlanId(
  sellingPlanId: string | number | null | undefined
): ISmartrrSellingPlan[] {
  const sellingPlanGroups = useSellingPlanGroups();

  return useMemo(() => {
    if (sellingPlanId === undefined || sellingPlanId === null) {
      return [];
    }

    const coercedShopifyId = shopifyGidToNumber(sellingPlanId);
    return flatten(
      sellingPlanGroups
        .filter(spg => spg.sellingPlans.some(sellingPlan => sellingPlan.shopifyNumericId === coercedShopifyId))
        .map(spg => spg.sellingPlans)
    );
  }, [sellingPlanGroups, sellingPlanId]);
}

// for "Add More"/"Swapping" get product list
export function useProductsListForSellingPlanId(
  sellingPlanId: string | number | undefined | null,
  includeAddOnable = true
): IPurchasable[] {
  const { purchasables, isLoading, isLoadingAdditionaly } = useCleanedAndSortedPurchables();
  const enablementMaps = useEnablementMapForSellingPlanId(sellingPlanId);

  return useMemo(() => {
    if (isLoading || isLoadingAdditionaly) {
      return [];
    }
    const shopifyProductIdsSet = new Set<number>(
      Object.keys(enablementMaps.sellingPlanGroupWithEnablementMap?.enablementMap || {})
        .concat(
          includeAddOnable ? Object.keys(enablementMaps.addonConfigWithEnablementMap?.enablementMap || {}) : []
        )
        .map(shopifyGidToNumber)
    );

    return purchasables
      .filter(purchasable => (purchasable.vnts || []).some(isVariantInStock)) // at least one variant in stock
      .filter(
        purchasable =>
          purchasable.shopifyId && shopifyProductIdsSet.has(shopifyGidToNumber(purchasable.shopifyId))
      );
  }, [sellingPlanId, purchasables, includeAddOnable, enablementMaps, isLoadingAdditionaly, isLoading]);
}

export const useVariantListForSellingPlanId = (
  sellingPlanId: string | undefined,
  delivery: PurchaseStateAndDelivery,
  includeAddOnable = true
) => {
  const enablementMaps = useEnablementMapForSellingPlanId(sellingPlanId);
  const subscribablePurchasables = useProductsListForSellingPlanId(sellingPlanId, includeAddOnable);
  const getPurchasablesForSubscribing = usePurchasablesForSubscribing(delivery, true);
  const purchasablesForSubscribing = getPurchasablesForSubscribing(delivery, subscribablePurchasables);

  return useMemo(() => {
    if (!enablementMaps.sellingPlanGroupWithEnablementMap?.variantIds) {
      return [] as IPurchasableVariant[];
    }

    const { variantIds } = enablementMaps.sellingPlanGroupWithEnablementMap;

    return (
      purchasablesForSubscribing.reduce((acc, p) => {
        const variants = (p.vnts || []).filter(
          v => isT(v.shopifyId) && !v.isDraftOrArchived && variantIds.includes(shopifyGidToNumber(v.shopifyId))
        );

        acc.push(...variants);
        return acc;
      }, [] as IPurchasableVariant[]) || []
    );
  }, [enablementMaps.sellingPlanGroupWithEnablementMap, purchasablesForSubscribing]);
};

// for "Add"/"Swap" side panel
export interface VariantPurchaseOptions {
  vnt: IPurchasableVariant;
  addonConfig: ISmartrrSellingPlan | undefined;
  trendingConfig: ISmartrrSellingPlan | undefined;
  sellingPlan: ISmartrrSellingPlan | undefined;
}
export function useVariantEnablementForSellingPlanId(
  purchasable: IPurchasable | undefined,
  sellingPlanId: string | number | undefined | null,
  includeAddOnable = true,
  includeTrending = false
): VariantPurchaseOptions[] {
  const enablementMaps = useEnablementMapForSellingPlanId(sellingPlanId);

  return useMemo(() => {
    if (!purchasable || sellingPlanId === undefined || sellingPlanId === null) {
      return [];
    }

    const addOnableVariantShopifyIds = new Set<number>(
      includeAddOnable ? enablementMaps.addonConfigWithEnablementMap?.variantIds || [] : []
    );

    const trendingVariantShopifyIds = new Set<number>(
      includeTrending ? enablementMaps.trendingListConfigWithEnablementMap?.variantIds || [] : []
    );

    const subscribableVariantShopifyIds = new Set<number>(
      enablementMaps.sellingPlanGroupWithEnablementMap?.variantIds || []
    );

    return compact(
      purchasable.vnts
        ?.filter(vnt => vnt.isActiveInShopify && !vnt.deletedAt)
        .map(vnt => {
          if (!vnt.shopifyId) {
            return null;
          }

          const coercedShopifyId = shopifyGidToNumber(vnt.shopifyId);
          const isAddonable = addOnableVariantShopifyIds.has(coercedShopifyId);
          const isSubscribable = subscribableVariantShopifyIds.has(coercedShopifyId);
          const isTrending = trendingVariantShopifyIds.has(coercedShopifyId);

          if (!isAddonable && !isSubscribable && !isTrending) {
            return null;
          }

          if (!isVariantInStock(vnt)) {
            return null;
          }

          return {
            vnt,
            addonConfig: isAddonable ? enablementMaps.addonConfig : undefined,
            trendingConfig: isTrending ? enablementMaps.trendingConfig : undefined,
            sellingPlan: isSubscribable ? enablementMaps.sellingPlan : undefined,
          } as VariantPurchaseOptions;
        }) ?? []
    );
  }, [enablementMaps, purchasable, sellingPlanId, includeAddOnable]);
}

export function useEnablementMapForSellingPlanId(sellingPlanId: string | number | undefined | null): {
  sellingPlan?: ISmartrrSellingPlan;
  addonConfig?: ISmartrrSellingPlan;
  trendingConfig?: ISmartrrSellingPlan;
  sellingPlanGroupWithEnablementMap?: ISmartrrSellingPlanGroupWithEnablementMap;
  addonConfigWithEnablementMap?: ISmartrrSellingPlanGroupWithEnablementMap;
  trendingListConfigWithEnablementMap?: ISmartrrSellingPlanGroupWithEnablementMap;
} {
  const enablementMaps = useEnablementMapsFromLiquid();

  return useMemo(() => {
    if (sellingPlanId === undefined || sellingPlanId === null) {
      return {};
    }

    const coercedSellingPlanId = shopifyGidToNumber(sellingPlanId);

    const sellingPlanGroupWithEnablementMap: ISmartrrSellingPlanGroupWithEnablementMap | undefined =
      enablementMaps.sellingPlanGroupsWithEnablementMap.find(spgWithEnablementMap =>
        spgWithEnablementMap.sellingPlans.some(
          sellingPlan => shopifyGidToNumber(sellingPlan.shopifyNumericId) === coercedSellingPlanId
        )
      );

    const sellingPlan = sellingPlanGroupWithEnablementMap?.sellingPlans.find(
      sellingPlan => shopifyGidToNumber(sellingPlan.shopifyNumericId) === coercedSellingPlanId
    );
    const addonConfig = enablementMaps.addonConfigWithEnablementMap?.sellingPlans[0];
    const trendingConfig = enablementMaps.trendingListConfigWithEnablementMap?.sellingPlans[0];

    return {
      sellingPlan,
      addonConfig,
      trendingConfig,
      sellingPlanGroupWithEnablementMap,
      addonConfigWithEnablementMap: enablementMaps.addonConfigWithEnablementMap,
      trendingListConfigWithEnablementMap: enablementMaps.trendingListConfigWithEnablementMap,
    };
  }, [sellingPlanId, enablementMaps]);
}

function useEnablementMapsFromLiquid(): {
  sellingPlanGroupsWithEnablementMap: ISmartrrSellingPlanGroupWithEnablementMap[];
  addonConfigWithEnablementMap?: ISmartrrSellingPlanGroupWithEnablementMap;
  trendingListConfigWithEnablementMap?: ISmartrrSellingPlanGroupWithEnablementMap;
} {
  const { purchasables } = useCleanedAndSortedPurchables();
  const sellingPlanGroups = useSellingPlanGroups();
  const sellingPlanGroupsByProductId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByProductId
  );
  const sellingPlanGroupsByVariantId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByVariantId
  );
  const sellingPlanGroupsWithEnablementMap = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsWithEnablementMap
  );

  return useMemo(() => {
    return {
      sellingPlanGroupsWithEnablementMap: sellingPlanGroupsWithEnablementMap.filter(
        sellingPlanGroup =>
          !sellingPlanGroup.name.includes(SELLING_PLAN_GROUP_ADDON_TAG) &&
          !sellingPlanGroup.name.includes(SELLING_PLAN_GROUP_TRENDING_LISTS_TAG)
      ),
      addonConfigWithEnablementMap: sellingPlanGroupsWithEnablementMap.find(sellingPlanGroup =>
        sellingPlanGroup.name.includes(SELLING_PLAN_GROUP_ADDON_TAG)
      ),
      trendingListConfigWithEnablementMap: sellingPlanGroupsWithEnablementMap.find(sellingPlanGroup =>
        sellingPlanGroup.name.includes(SELLING_PLAN_GROUP_TRENDING_LISTS_TAG)
      ),
    };
  }, [
    purchasables,
    sellingPlanGroups,
    sellingPlanGroupsByProductId,
    sellingPlanGroupsByVariantId,
    sellingPlanGroupsWithEnablementMap,
  ]);
}

export function useSellingPlanGroups(): ISmartrrSellingPlanGroup[] {
  const { purchasables } = useCleanedAndSortedPurchables();
  const sellingPlanGroupsByProductId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByProductId
  );
  const sellingPlanGroupsByVariantId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByVariantId
  );

  return useMemo(() => {
    const productSellingPlanGroupsEntries = Object.entries(sellingPlanGroupsByProductId);
    const variantSellingPlanGroupsEntries = Object.entries(sellingPlanGroupsByVariantId);
    return uniqBy(
      flatten(productSellingPlanGroupsEntries.concat(variantSellingPlanGroupsEntries).map(([, spg]) => spg)),
      ({ shopifyNumericId }) => shopifyNumericId
    ).filter(spg => spg.appId === SHOPIFY_APP_ID);
  }, [purchasables, sellingPlanGroupsByProductId, sellingPlanGroupsByVariantId]);
}

// finds the first instance of a selling plan group that is an addon
export const useAddonSellingPlanGroup = () => {
  const addonConfigs = useSmartrrCustomerSelector(state => state.sellingPlanGroups.addOnsConfigs);

  return useMemo(() => {
    if (addonConfigs.length) {
      return addonConfigs[0];
    }
  }, [addonConfigs]);
};

export const useTrendingSellingPlanGroup = () => {
  const trendingConfigs = useSmartrrCustomerSelector(state => state.sellingPlanGroups.trendingConfigs);

  return useMemo(() => {
    if (trendingConfigs.length) {
      return trendingConfigs[0];
    }
  }, [trendingConfigs]);
};

export const useConfigToSellingPlanGroup = (configType: SellingPlanConfigs) => {
  const trendingConfigs = useTrendingSellingPlanGroup();
  const addonConfig = useAddonSellingPlanGroup();
  if (configType === SellingPlanConfigs.ADDON) {
    return addonConfig;
  } else if (configType === SellingPlanConfigs.TRENDING) {
    return trendingConfigs;
  }
  return;
};
